import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Polyline,
  Polygon,
  CircleMarker,
  useMapEvents,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Row,
  Space,
  Typography,
  message,
} from 'antd';
import {
  AimOutlined,
  CheckOutlined,
  DeleteOutlined,
  DragOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  geoApi,
  BuildingFeature,
  DistrictAddresses,
  DistrictFeature,
  StreetFeature,
  FeatureCollection,
  GeoJSONPolygon,
} from '../../api/geo';
import { Card, Empty, Tag, Tooltip } from 'antd';
import { HomeOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const AKSAI_CENTER: [number, number] = [51.166, 53.02];
const AKSAI_ZOOM = 14;

// Приятная палитра для полигонов районов (будем выбирать по id % length)
const DISTRICT_COLORS = [
  'var(--l-accent)', '#f5222d', 'var(--l-ok)', 'var(--l-warn)', 'var(--l-violet)',
  'var(--l-teal)', '#eb2f96', 'var(--l-warn)', '#2f54eb', '#a0d911',
];

const colorFor = (id: number) => DISTRICT_COLORS[id % DISTRICT_COLORS.length];

// Хук для захвата кликов в режиме рисования
const DrawClickHandler: React.FC<{
  enabled: boolean;
  onPoint: (lat: number, lng: number) => void;
}> = ({ enabled, onPoint }) => {
  useMapEvents({
    click: (e) => {
      if (enabled) onPoint(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

type DrawPoint = { lat: number; lng: number };

const DistrictsMap: React.FC = () => {
  const [districts, setDistricts] =
    useState<FeatureCollection<DistrictFeature> | null>(null);
  const [streets, setStreets] =
    useState<FeatureCollection<StreetFeature> | null>(null);
  const [loading, setLoading] = useState(false);

  const [drawMode, setDrawMode] = useState(false);
  const [drawPoints, setDrawPoints] = useState<DrawPoint[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null); // если !== null — редактируем полигон существующего района
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const geojsonKeyRef = useRef(0);

  // ── Район → улицы → дома (требование владельца 04.08.2026) ──
  // Выбрали район — подсветились ЕГО дома (у нас есть контуры) и показался
  // список улиц с фактическими диапазонами номеров. Улица в двух районах —
  // норма: она появится в обоих с разными диапазонами.
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [addresses, setAddresses] = useState<DistrictAddresses | null>(null);
  const [dBuildings, setDBuildings] =
    useState<FeatureCollection<BuildingFeature> | null>(null);
  const [addrLoading, setAddrLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  const selectDistrict = useCallback(async (id: number | null) => {
    setSelectedId(id);
    setAddresses(null);
    setDBuildings(null);
    if (id == null) return;
    setAddrLoading(true);
    try {
      const [a, b] = await Promise.all([
        geoApi.districtAddresses(id),
        geoApi.districtBuildings(id),
      ]);
      setAddresses(a.data);
      setDBuildings(b.data);
    } catch {
      messageApi.error('Не удалось загрузить адреса района');
    }
    setAddrLoading(false);
  }, [messageApi]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        geoApi.districtsGeoJSON(),
        geoApi.streetsGeoJSON(true),
      ]);
      setDistricts(d.data);
      setStreets(s.data);
      geojsonKeyRef.current += 1;
    } catch (err) {
      messageApi.error('Ошибка загрузки карты');
    }
    setLoading(false);
  }, [messageApi]);

  useEffect(() => {
    load();
  }, [load]);

  // --- Рисование ---

  const startDraw = (districtId: number | null = null) => {
    setDrawMode(true);
    setEditingId(districtId);
    setDrawPoints([]);
    messageApi.info('Кликайте по карте, чтобы добавить точки. Двойной клик — завершить.');
  };

  const cancelDraw = () => {
    setDrawMode(false);
    setEditingId(null);
    setDrawPoints([]);
  };

  const handleDrawPoint = (lat: number, lng: number) => {
    setDrawPoints((prev) => [...prev, { lat, lng }]);
  };

  const finishDraw = () => {
    if (drawPoints.length < 3) {
      messageApi.warning('Нужно минимум 3 точки для полигона');
      return;
    }
    setSaveOpen(true);
    if (editingId !== null) {
      const f = districts?.features.find((x) => x.id === editingId);
      saveForm.setFieldsValue({
        name_ru: f?.properties.name_ru,
        name_kk: f?.properties.name_kk ?? '',
      });
    } else {
      saveForm.resetFields();
    }
  };

  const polygonGeoJSON = (): GeoJSONPolygon => {
    // GeoJSON: [[ [lon, lat], ..., [lon, lat] ]] — замыкаем кольцо
    const ring = drawPoints.map((p) => [p.lng, p.lat]);
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) ring.push([first[0], first[1]]);
    return { type: 'Polygon', coordinates: [ring] };
  };

  const savePolygon = async () => {
    try {
      const values = await saveForm.validateFields();
      const geom = polygonGeoJSON();
      let districtId: number;
      if (editingId !== null) {
        await geoApi.updateDistrict(editingId, { ...values, geom });
        districtId = editingId;
        messageApi.success('Полигон обновлён');
      } else {
        const res = await geoApi.createDistrict({ ...values, geom });
        districtId = res.data.id;
        messageApi.success('Район создан');
      }
      // сразу автопривязка улиц
      const assigned = await geoApi.assignStreets(districtId, 'replace');
      messageApi.success(`Привязано улиц: ${assigned.data.assigned_count}`);
      setSaveOpen(false);
      cancelDraw();
      await load();
    } catch (err: any) {
      if (err?.errorFields) return; // ошибка валидации формы
      messageApi.error(err?.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const deleteDistrict = async (id: number) => {
    try {
      await geoApi.deleteDistrict(id);
      messageApi.success('Район удалён');
      await load();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const reassign = async (id: number) => {
    try {
      const res = await geoApi.assignStreets(id, 'replace');
      messageApi.success(`Привязано улиц: ${res.data.assigned_count}`);
      await load();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка');
    }
  };

  // --- Данные для рендера карты ---

  const districtsForMap = useMemo(() => {
    if (!districts) return { type: 'FeatureCollection', features: [] };
    // в режиме редактирования прячем тот, что правим (чтоб старый полигон не мешал)
    return {
      ...districts,
      features: districts.features
        .filter((f) => f.geometry)
        .filter((f) => (editingId !== null ? f.id !== editingId : true)),
    };
  }, [districts, editingId]);

  const streetsForMap = useMemo(() => {
    if (!streets) return { type: 'FeatureCollection', features: [] };
    return {
      ...streets,
      features: streets.features.filter((f) => f.geometry),
    };
  }, [streets]);

  const unassignedCount =
    streets?.features.filter((f) => f.properties.district_id === null).length ?? 0;

  const drawPolygonLatLngs: [number, number][] = drawPoints.map((p) => [p.lat, p.lng]);

  return (
    <div style={{ padding: 16 }}>
      {contextHolder}
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col flex="auto">
          <Title level={4} style={{ margin: 0 }}>Районы на карте</Title>
          <Text type="secondary">
            Нарисуйте полигон — улицы, чьи центроиды попали внутрь, привяжутся автоматически.
          </Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Обновить
            </Button>
            <Tooltip title="Дом → район по контуру, улица → район по большинству её домов, справочник — по точке">
              <Button
                icon={<ThunderboltOutlined />}
                loading={recomputing}
                onClick={async () => {
                  setRecomputing(true);
                  try {
                    const { data } = await geoApi.recomputeLinks();
                    messageApi.success(
                      `Пересчитано: домов ${data.updated.buildings}, улиц ${data.updated.streets}, ` +
                      `организаций ${data.updated.directory}. ` +
                      `Улиц с районом ${data.totals.streets_with}/${data.totals.streets_total}, ` +
                      `домов ${data.totals.buildings_with}/${data.totals.buildings_total}.`,
                      6,
                    );
                    await load();
                    if (selectedId != null) await selectDistrict(selectedId);
                  } catch {
                    messageApi.error('Не удалось пересчитать привязки');
                  }
                  setRecomputing(false);
                }}
              >
                Пересчитать привязки
              </Button>
            </Tooltip>
            {!drawMode ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => startDraw(null)}
              >
                Нарисовать новый район
              </Button>
            ) : (
              <>
                <Button icon={<CheckOutlined />} type="primary" onClick={finishDraw}
                  disabled={drawPoints.length < 3}>
                  Завершить ({drawPoints.length} т.)
                </Button>
                <Button icon={<StopOutlined />} onClick={cancelDraw} danger>
                  Отмена
                </Button>
              </>
            )}
          </Space>
        </Col>
      </Row>

      {drawMode && (
        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          icon={<DragOutlined />}
          message={
            <>
              Режим рисования: кликайте по карте по периметру района. Минимум 3 точки.
              {' '}
              <Button type="link" size="small" onClick={() => setDrawPoints([])}>
                Очистить точки
              </Button>
            </>
          }
        />
      )}

      <Row gutter={16}>
        <Col xs={24} md={17}>
          <div style={{ height: 'calc(100vh - 240px)', minHeight: 500, border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
            <MapContainer
              center={AKSAI_CENTER}
              zoom={AKSAI_ZOOM}
              style={{ height: '100%', width: '100%' }}
              doubleClickZoom={!drawMode}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                attribution="© OpenStreetMap · © CARTO"
              />

              {/* Улицы (линии) */}
              <GeoJSON
                key={`streets-${geojsonKeyRef.current}`}
                data={streetsForMap as any}
                style={(feat: any) => {
                  const did = feat?.properties?.district_id;
                  if (did) {
                    return { color: colorFor(did), weight: 3, opacity: 0.85 };
                  }
                  return { color: '#8c8c8c', weight: 2, opacity: 0.55, dashArray: '4 4' };
                }}
                onEachFeature={(feat: any, layer: any) => {
                  const p = feat.properties;
                  layer.bindTooltip(
                    `${p.name_ru}${p.district_id ? '' : ' (без района)'}`,
                    { sticky: true },
                  );
                }}
              />

              {/* Центроиды улиц — маленькие серые точки */}
              {streets?.features.map((f) => {
                const c = f.properties.centroid;
                if (!c) return null;
                return (
                  <CircleMarker
                    key={`c-${f.id}`}
                    center={[c[1], c[0]]}
                    radius={2}
                    pathOptions={{ color: '#595959', fillOpacity: 1, weight: 1 }}
                  />
                );
              })}

              {/* Существующие районы — полигоны */}
              <GeoJSON
                key={`districts-${geojsonKeyRef.current}-${editingId}-${selectedId}`}
                data={districtsForMap as any}
                style={(feat: any) => ({
                  color: colorFor(feat.id),
                  weight: selectedId === feat.id ? 4 : 2,
                  fillOpacity: selectedId == null ? 0.12 : (selectedId === feat.id ? 0.06 : 0.03),
                })}
                onEachFeature={(feat: any, layer: any) => {
                  const p = feat.properties;
                  const html = `<b>${p.name_ru}</b><br/>улиц: ${p.streets_count}`
                    + (p.buildings_count != null ? `<br/>домов: ${p.buildings_count}` : '');
                  layer.bindTooltip(html, { sticky: true });
                  layer.on('click', () => selectDistrict(feat.id));
                }}
              />

              {/* Дома выбранного района — подсветка по контуру, а не «примерно тут» */}
              {dBuildings && selectedId != null && (
                <GeoJSON
                  key={`bld-${selectedId}-${dBuildings.features.length}`}
                  data={dBuildings as any}
                  style={() => ({
                    color: colorFor(selectedId),
                    weight: 1,
                    fillOpacity: 0.55,
                  })}
                  onEachFeature={(feat: any, layer: any) => {
                    const p = feat.properties;
                    const bits = [`<b>${p.street ?? ''} ${p.house_number ?? ''}</b>`];
                    if (p.purpose_name) bits.push(p.purpose_name);
                    if (p.floors) bits.push(`${p.floors} эт.`);
                    layer.bindTooltip(bits.join('<br/>'), { sticky: true });
                  }}
                />
              )}

              {/* Обработчик кликов в режиме рисования */}
              <DrawClickHandler enabled={drawMode} onPoint={handleDrawPoint} />

              {/* Рисуемый полигон (в процессе) */}
              {drawPoints.length >= 2 && drawPoints.length < 3 && (
                <Polyline positions={drawPolygonLatLngs} pathOptions={{ color: '#fa541c', weight: 3 }} />
              )}
              {drawPoints.length >= 3 && (
                <Polygon
                  positions={drawPolygonLatLngs}
                  pathOptions={{ color: '#fa541c', weight: 3, fillOpacity: 0.15 }}
                />
              )}
              {drawPoints.map((p, i) => (
                <CircleMarker
                  key={`d-${i}`}
                  center={[p.lat, p.lng]}
                  radius={5}
                  pathOptions={{ color: '#fa541c', fillColor: '#fff', fillOpacity: 1, weight: 2 }}
                />
              ))}
            </MapContainer>
          </div>
        </Col>

        <Col xs={24} md={7}>
          <div style={{ marginBottom: 12 }}>
            <div>Всего улиц с координатами: {streets?.features.filter(f => f.geometry).length ?? 0}</div>
            <div>Без района: {unassignedCount}</div>
          </div>
          <List
            header={<b>Районы</b>}
            bordered
            size="small"
            dataSource={districts?.features ?? []}
            renderItem={(f) => (
              <List.Item
                actions={[
                  <Button
                    key="draw"
                    size="small"
                    icon={<AimOutlined />}
                    title="Перерисовать полигон"
                    onClick={() => startDraw(f.id)}
                  />,
                  <Button
                    key="assign"
                    size="small"
                    icon={<ThunderboltOutlined />}
                    title="Переназначить улицы"
                    disabled={!f.geometry}
                    onClick={() => reassign(f.id)}
                  />,
                  <Popconfirm
                    key="del"
                    title="Удалить район?"
                    description="Улицы станут без района"
                    onConfirm={() => deleteDistrict(f.id)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>,
                ]}
              >
                <div
                  onClick={() => selectDistrict(selectedId === f.id ? null : f.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    cursor: 'pointer',
                    background: selectedId === f.id ? 'var(--l-accent-bg)' : undefined,
                    borderRadius: 6, padding: selectedId === f.id ? '2px 6px' : undefined,
                  }}
                >
                  <div style={{
                    width: 12, height: 12, borderRadius: '50%',
                    background: colorFor(f.id), flexShrink: 0,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                      {f.properties.name_ru}
                      {f.properties.source === '2gis' && (
                        <Tag style={{ marginLeft: 6 }}>2ГИС</Tag>
                      )}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      улиц: {f.properties.streets_count}
                      {f.properties.buildings_count != null &&
                        ` · домов: ${f.properties.buildings_count}`}
                      {!f.geometry && <span style={{ color: 'var(--l-warn)', marginLeft: 6 }}>• без границ</span>}
                    </Text>
                  </div>
                </div>
              </List.Item>
            )}
          />

          {/* Адреса выбранного района: улицы с фактическими диапазонами домов */}
          {selectedId != null && (
            <Card
              size="small"
              loading={addrLoading}
              style={{ marginTop: 12 }}
              title={
                <Space>
                  <HomeOutlined />
                  <span>{addresses?.district_name ?? 'Адреса района'}</span>
                </Space>
              }
              extra={<Button type="link" size="small" onClick={() => selectDistrict(null)}>
                снять выбор
              </Button>}
            >
              {addresses && addresses.streets.length === 0 && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="В границах района нет ни одного дома с адресом"
                />
              )}
              {addresses && addresses.streets.length > 0 && (
                <>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    улиц {addresses.streets_count} · домов {addresses.houses_count}
                  </Text>
                  <div style={{ maxHeight: 320, overflow: 'auto', marginTop: 8 }}>
                    {addresses.streets.map((s) => (
                      <div key={s.street} style={{ marginBottom: 7, lineHeight: 1.45 }}>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>
                          {s.street}
                          {!s.is_primary_district && (
                            <Tooltip title="Часть домов этой улицы лежит в другом районе — это норма, а не ошибка данных">
                              <Tag style={{ marginLeft: 6 }}>часть улицы</Tag>
                            </Tooltip>
                          )}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          дома {s.ranges.join(', ')} ({s.houses})
                        </Text>
                      </div>
                    ))}
                  </div>
                  {addresses.houses_without_district_total > 0 && (
                    <Text type="secondary" style={{ fontSize: 11.5 }}>
                      Домов вне всех районов по городу: {addresses.houses_without_district_total}
                    </Text>
                  )}
                </>
              )}
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        title={editingId !== null ? 'Обновить район' : 'Новый район'}
        open={saveOpen}
        onCancel={() => setSaveOpen(false)}
        onOk={savePolygon}
        okText="Сохранить и привязать улицы"
        cancelText="Отмена"
      >
        <Form form={saveForm} layout="vertical">
          <Form.Item name="name_ru" label="Название (рус)" rules={[{ required: true }]}>
            <Input placeholder="например: Аксай — частный сектор" />
          </Form.Item>
          <Form.Item name="name_kk" label="Название (каз)">
            <Input placeholder="Ақсай — ..." />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            message={`Полигон: ${drawPoints.length} точек. После сохранения все улицы, чьи центроиды попадут внутрь, привяжутся к этому району.`}
          />
        </Form>
      </Modal>
    </div>
  );
};

export default DistrictsMap;
